import { supabaseClient } from "@/lib/supabase";
import {
    DEFAULT_LOCALE,
    normaliseLocaleTag,
    type FormTranslationBundle,
    type SupportedLocale,
} from "@patient-watch/i18n";
import type { FormViewT, PageViewT } from "@patient-watch/types";

function collectFormStructure(form: FormViewT) {
    const pageIds: string[] = [];
    const inputIds: string[] = [];
    const blockIds: string[] = [];

    for (const page of (form.pages ?? []) as PageViewT[]) {
        if (page.page_id) {
            pageIds.push(page.page_id);
        }
        for (const block of page.blocks ?? []) {
            if (block.block_id) {
                blockIds.push(block.block_id);
            }
        }
        for (const input of page.inputs ?? []) {
            if (input.input_id) {
                inputIds.push(input.input_id);
            }
        }
    }

    return { pageIds, inputIds, blockIds };
}

/** Load translation rows for a canonical form and locale. */
export async function fetchFormTranslations(form: FormViewT, locale: SupportedLocale): Promise<FormTranslationBundle> {
    const normalizedLocale = normaliseLocaleTag(locale) ?? DEFAULT_LOCALE;
    if (normalizedLocale === DEFAULT_LOCALE) {
        return {};
    }

    const formId = form.form_id;
    const { pageIds, inputIds, blockIds } = collectFormStructure(form);

    const [formResult, pageResult, inputResult, blockResult] = await Promise.all([
        supabaseClient
            .from("form_translations")
            .select("form_name, form_description, form_instructions, form_caption")
            .eq("form_id", formId)
            .eq("locale", normalizedLocale)
            .maybeSingle(),
        pageIds.length > 0
            ? supabaseClient
                  .from("page_translations")
                  .select("page_id, page_label, page_description")
                  .eq("locale", normalizedLocale)
                  .in("page_id", pageIds)
            : Promise.resolve({ data: [], error: null }),
        inputIds.length > 0
            ? supabaseClient
                  .from("input_translations")
                  .select("input_id, input_label, input_caption, input_description, scale_options, options")
                  .eq("locale", normalizedLocale)
                  .in("input_id", inputIds)
            : Promise.resolve({ data: [], error: null }),
        blockIds.length > 0
            ? supabaseClient
                  .from("block_translations")
                  .select("block_id, content, caption")
                  .eq("locale", normalizedLocale)
                  .in("block_id", blockIds)
            : Promise.resolve({ data: [], error: null }),
    ]);

    if (formResult.error) throw formResult.error;
    if (pageResult.error) throw pageResult.error;
    if (inputResult.error) throw inputResult.error;
    if (blockResult.error) throw blockResult.error;

    return {
        form: formResult.data,
        pages: pageResult.data ?? [],
        inputs: (inputResult.data ?? []) as FormTranslationBundle["inputs"],
        blocks: blockResult.data ?? [],
    };
}
